import { TIERS, TIER_LIMITS } from "../constants/membership";

export function normalizeLobbyMapKey(mapName) {
    return String(mapName || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
}

export function getMinMapsSelectedForTier(tier) {
    const limits = TIER_LIMITS[tier] || TIER_LIMITS[TIERS.FREE];
    const minMaps = Number(limits.minMapsSelected);
    return Number.isFinite(minMaps) && minMaps > 0 ? minMaps : null;
}

export function buildMinMapsToastMessage(minMaps) {
    if (minMaps === 1) {
        return "Keep at least 1 map active to stay in queue.";
    }
    return `Keep at least ${minMaps} maps active to stay in queue.`;
}

/**
 * Returns false when deselecting `mapName` would drop below the plan minimum.
 */
export function canToggleMapSelection({
    selectedMaps = [],
    mapName,
    tier,
} = {}) {
    const normalized = normalizeLobbyMapKey(mapName);
    if (!normalized) return false;

    const current = (Array.isArray(selectedMaps) ? selectedMaps : [])
        .map((map) => normalizeLobbyMapKey(map))
        .filter(Boolean);
    const isSelected = current.includes(normalized);
    if (!isSelected) {
        return { allowed: true, nextCount: current.length + 1 };
    }

    const minMaps = getMinMapsSelectedForTier(tier);
    const nextCount = current.length - 1;
    if (minMaps != null && nextCount < minMaps) {
        return {
            allowed: false,
            nextCount,
            minMaps,
            message: buildMinMapsToastMessage(minMaps),
        };
    }

    return { allowed: true, nextCount };
}

/**
 * Clearing the pool is only valid when the plan has no minimum (or min is 0).
 */
export function canClearAllMaps({ tier } = {}) {
    const minMaps = getMinMapsSelectedForTier(tier);
    if (minMaps != null && minMaps > 0) {
        return {
            allowed: false,
            minMaps,
            message: buildMinMapsToastMessage(minMaps),
        };
    }
    return { allowed: true };
}

export function getMinLocationsSelectedForTier(tier, availableCount) {
    const limits = TIER_LIMITS[tier] || TIER_LIMITS[TIERS.FREE];
    const minLocations = Number(limits.minLocationsSelected);
    if (!Number.isFinite(minLocations) || minLocations <= 0) return null;
    const available = Number(availableCount);
    if (Number.isFinite(available) && available > 0) {
        return Math.min(minLocations, available);
    }
    return minLocations;
}

export function buildMinLocationsToastMessage(minLocations) {
    if (minLocations === 1) {
        return "Keep at least 1 region active to stay in queue.";
    }
    return `Keep at least ${minLocations} regions active to stay in queue.`;
}

export function normalizeLobbyLocationId(locationId) {
    return String(locationId || "").trim();
}

/**
 * Returns whether deselecting `locationId` would drop below the plan minimum.
 * `availableCount` caps the minimum when fewer regions are enabled than the plan asks for.
 */
export function canToggleLocationSelection({
    selectedLocations = [],
    locationId,
    tier,
    availableCount,
} = {}) {
    const normalized = normalizeLobbyLocationId(locationId);
    if (!normalized) return { allowed: false, nextCount: 0 };

    const current = (Array.isArray(selectedLocations) ? selectedLocations : [])
        .map((id) => normalizeLobbyLocationId(id))
        .filter(Boolean);
    const isSelected = current.includes(normalized);
    if (!isSelected) {
        return { allowed: true, nextCount: current.length + 1 };
    }

    const minLocations = getMinLocationsSelectedForTier(tier, availableCount);
    const nextCount = current.length - 1;
    if (minLocations != null && nextCount < minLocations) {
        return {
            allowed: false,
            nextCount,
            minLocations,
            message: buildMinLocationsToastMessage(minLocations),
        };
    }

    return { allowed: true, nextCount };
}

export function canClearAllLocations({ tier, availableCount } = {}) {
    const minLocations = getMinLocationsSelectedForTier(tier, availableCount);
    if (minLocations != null && minLocations > 0) {
        return {
            allowed: false,
            minLocations,
            message: buildMinLocationsToastMessage(minLocations),
        };
    }
    return { allowed: true };
}
